import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, keyframes } from '@mui/material';
import MathText from './MathText';

// Vibrancy kept at ~1/4 of the previous peak glow intensity.
const mathGlowPulse = (glowColor) => keyframes`
    0%, 100% {
        filter: drop-shadow(0 0 4px ${glowColor}1a) drop-shadow(0 0 8px ${glowColor}0d);
    }
    50% {
        filter: drop-shadow(0 0 6px ${glowColor}2e) drop-shadow(0 0 12px ${glowColor}18);
    }
`;

// Renders display math at a large size, scaling down to fit the container (no scrollbars).
export default function FitMathDisplay({
    text,
    minScale = 0.55,
    accentColor = '#ffffff',
    glowColor = accentColor,
    vibrant = true,
}) {
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const fit = () => {
            const outer = outerRef.current;
            const inner = innerRef.current;
            if (!outer || !inner) return;

            inner.style.transform = 'scale(1)';
            const available = outer.clientWidth;
            const content = inner.scrollWidth;
            if (content <= available) {
                setScale(1);
                return;
            }
            const next = Math.max(minScale, available / content);
            setScale(next);
        };

        fit();
        const observer = new ResizeObserver(fit);
        if (outerRef.current) observer.observe(outerRef.current);
        return () => observer.disconnect();
    }, [text, minScale, accentColor, glowColor]);

    const katexGlow = vibrant
        ? `0 0 4px ${glowColor}28, 0 0 10px ${glowColor}14`
        : 'none';

    return (
        <Box
            ref={outerRef}
            sx={{
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                ref={innerRef}
                sx={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                    animation: vibrant ? `${mathGlowPulse(glowColor)} 3.2s ease-in-out infinite` : 'none',
                    '& .katex': {
                        fontSize: 'clamp(1.85rem, 3.8vw + 0.75rem, 3.75rem)',
                        color: `${accentColor} !important`,
                        textShadow: katexGlow,
                    },
                    '& .katex .mord, & .katex .mop, & .katex .mbin, & .katex .mrel, & .katex .mopen, & .katex .mclose, & .katex .mpunct, & .katex .minner, & .katex .mord.mtight': {
                        color: `${accentColor} !important`,
                    },
                    '& .katex-display': { margin: 0, overflow: 'hidden' },
                    '& .math-display': { margin: 0 },
                }}
            >
                <MathText text={text} display />
            </Box>
        </Box>
    );
}
